/**
 * Theme system — CSS custom properties for dark/light themes.
 * Applied via [data-theme] attribute on document root.
 * All layout, panel, and component styles are injected here.
 */

const DARK_THEME: Record<string, string> = {
  // Backgrounds — neutral dark
  '--bg-primary': '#080808',
  '--bg-secondary': '#0e0e0e',
  '--bg-tertiary': '#141414',
  '--bg-surface': '#141414',
  '--bg-overlay': 'rgba(0, 0, 0, 0.6)',
  '--bg-hover': '#1a1a1a',

  // Text — neutral, no blue tint
  '--text-primary': '#e0e0e0',
  '--text-secondary': '#999999',
  '--text-muted': '#777777',
  '--text-dim': '#555555',
  '--text-accent': '#06b6d4',
  '--text-logo': '#ffffff',

  // Borders — neutral
  '--border-primary': '#222222',
  '--border-secondary': '#1a1a1a',

  // Accent — cyan/teal (modern defense intelligence aesthetic)
  '--accent': '#06b6d4',
  '--accent-hover': '#22d3ee',
  '--accent-bg': 'rgba(6, 182, 212, 0.15)',
  '--accent-border': 'rgba(6, 182, 212, 0.4)',
  '--accent-blue': '#06b6d4',
  '--accent-red': '#ef4444',
  '--accent-amber': '#f59e0b',
  '--accent-green': '#22c55e',
  '--accent-purple': '#a855f7',

  // Severity — unchanged
  '--severity-critical': '#ef4444',
  '--severity-high': '#f97316',
  '--severity-medium': '#eab308',
  '--severity-low': '#22c55e',
  '--severity-info': '#6b7280',

  // Tempo indicator colors
  '--tempo-escalating': '#ef4444',
  '--tempo-elevated': '#f59e0b',
  '--tempo-steady': '#06b6d4',
  '--tempo-calming': '#22c55e',

  '--font-ui': "'Inter Variable', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  '--font-mono': "'JetBrains Mono Variable', 'JetBrains Mono', 'Fira Code', 'Consolas', monospace",

  '--spacing-xs': '4px',
  '--spacing-sm': '8px',
  '--spacing-md': '16px',
  '--spacing-lg': '24px',
  '--spacing-xl': '32px',

  '--radius-sm': '4px',
  '--radius-md': '8px',
  '--radius-lg': '12px',

  '--shadow-sm': '0 1px 3px rgba(0, 0, 0, 0.3)',
  '--shadow-md': '0 4px 12px rgba(0, 0, 0, 0.4)',
  '--shadow-lg': '0 8px 24px rgba(0, 0, 0, 0.5)',

  '--header-height': '40px',
  '--sub-header-height': '28px',
  '--bottom-panel-height': '350px',
};

/**
 * Apply dark theme CSS custom properties to the root element.
 * Light theme removed — intel tool always uses dark UI to match the dark map tiles.
 * Signature kept for backward compatibility with callers.
 */
export function applyTheme(_theme?: 'dark' | 'light'): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', 'dark');

  for (const [prop, value] of Object.entries(DARK_THEME)) {
    root.style.setProperty(prop, value);
  }
}

/** Inject base CSS that references the custom properties. */
export function injectBaseStyles(): void {
  const style = document.createElement('style');
  style.id = 'sentinel-base-styles';
  style.textContent = BASE_CSS;
  document.head.appendChild(style);
}

const BASE_CSS = `
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: var(--font-ui);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.4;
}

/* ============ App Shell ============ */

#app {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ============ Header ============ */

#header-bar {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-primary);
  z-index: 100;
  user-select: none;
  height: var(--header-height);
  flex-shrink: 0;
}

.header-logo {
  font-family: var(--font-mono);
  font-size: 15px;
  font-weight: 700;
  color: var(--text-logo);
  letter-spacing: 2px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.header-divider {
  width: 1px;
  height: 20px;
  background: var(--border-primary);
  flex-shrink: 0;
}

.desk-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.desk-name {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  margin-right: 4px;
  white-space: nowrap;
}

.desk-btn {
  width: 28px;
  height: 28px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
}

.desk-btn svg {
  width: 14px;
  height: 14px;
}

.desk-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.desk-btn.active {
  background: var(--accent-bg);
  border-color: var(--accent-border);
  color: var(--accent);
}

/* Desk selector modal (first launch) */
.desk-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.desk-modal {
  background: var(--bg-secondary, #161616);
  border: 1px solid var(--border-primary, #2a2a2a);
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
}

.desk-modal-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary, #e0e0e0);
}

.desk-modal-subtitle {
  margin: 0 0 24px;
  font-size: 13px;
  color: var(--text-muted, #777);
  line-height: 1.5;
}

.desk-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.desk-modal-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 12px;
  background: var(--bg-tertiary, #1c1c1c);
  border: 1px solid var(--border-primary, #2a2a2a);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  color: var(--text-primary, #e0e0e0);
}

.desk-modal-card:hover {
  border-color: var(--accent, #06b6d4);
  background: var(--bg-hover, #222);
}

.desk-modal-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.desk-modal-card-icon svg {
  width: 28px;
  height: 28px;
}

.desk-modal-card:hover .desk-modal-card-icon {
  color: var(--accent);
}

.desk-modal-card-name {
  font-size: 13px;
  font-weight: 600;
}

.desk-modal-card-desc {
  font-size: 11px;
  color: var(--text-muted, #777);
  text-align: center;
  line-height: 1.4;
}

.header-search-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  max-width: 280px;
  height: 28px;
  padding: 0 10px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.header-search-trigger:hover {
  border-color: var(--accent-border);
  color: var(--text-secondary);
}

.header-search-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 0;
}

.header-search-text {
  flex: 1;
  text-align: left;
  font-size: 11px;
}

.header-search-kbd {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border-primary);
  color: var(--text-dim);
  flex-shrink: 0;
}

.header-spacer {
  flex: 1;
}

.header-btn {
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  flex-shrink: 0;
}

.header-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

/* ============ Sub-Header Bar ============ */

#sub-header-bar {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-md);
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
  height: var(--sub-header-height);
  flex-shrink: 0;
  z-index: 100;
  user-select: none;
}

.sub-header-title {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.sub-header-sep {
  width: 1px;
  height: 14px;
  background: var(--border-primary);
  flex-shrink: 0;
}

.sub-header-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.sub-header-spacer {
  flex: 1;
}

.sub-header-btn {
  width: 24px;
  height: 24px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  flex-shrink: 0;
}

.sub-header-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.utc-clock {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

/* ============ Status Indicators ============ */

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.online {
  background: var(--accent-green);
  box-shadow: 0 0 4px var(--accent-green);
}

.status-dot.offline {
  background: var(--accent-red);
  box-shadow: 0 0 4px var(--accent-red);
}

/* ============ Map Region ============ */

#map-region {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

#map-container {
  position: absolute;
  inset: 0;
  z-index: 0;
}

/* Canvas host owned by MapContainer — wiped on flat/globe mode switches,
   so overlay UI (controls, floating panels) must never live inside it. */
#map-canvas {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.maplibregl-canvas {
  outline: none;
}

/* MapLibre attribution — dark to blend with map */
.maplibregl-ctrl-attrib {
  background: rgba(0, 0, 0, 0.5) !important;
  color: var(--text-muted) !important;
  font-size: 10px !important;
  backdrop-filter: blur(4px);
}

.maplibregl-ctrl-attrib a {
  color: var(--text-muted) !important;
}

.maplibregl-ctrl-attrib-button {
  background-color: rgba(0, 0, 0, 0.5) !important;
  filter: invert(1) brightness(0.6);
}

/* ============ Floating Layers Overlay ============ */

#floating-layers {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 175px;
  max-height: calc(100% - 80px);
  background: rgba(2, 10, 8, 0.88);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-md);
  backdrop-filter: blur(12px);
  z-index: 10;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: max-height 200ms ease;
}

#floating-layers.collapsed .floating-layers-body {
  display: none;
}

#floating-layers.collapsed {
  max-height: none;
}

.floating-layers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
}

.floating-layers-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.floating-layers-title {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.floating-layers-toggle {
  font-size: 10px;
  color: var(--text-muted);
  transition: transform 0.2s;
}

.floating-layers-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Compact overrides for layer control inside floating panel */
#floating-layers .layer-category-header {
  padding: 5px 8px !important;
  font-size: 10px !important;
}

#floating-layers .layer-row {
  padding: 3px 8px 3px 22px !important;
  font-size: 10px !important;
}

#floating-layers .layer-category-badge {
  font-size: 9px !important;
  padding: 0px 4px !important;
}

/* ============ Floating Right Overlay (detail, country, settings) ============ */

#overlay-right {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 360px;
  max-height: calc(100% - 24px);
  z-index: 15;
  pointer-events: none;
}

#overlay-right .panel-inner {
  pointer-events: auto;
  background: rgba(2, 10, 8, 0.92);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-md);
  backdrop-filter: blur(12px);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  max-height: calc(100vh - 150px);
}

/* ============ Bottom Panel Grid ============ */

#bottom-panel-grid {
  display: grid;
  grid-template-columns: 1fr;
  height: var(--bottom-panel-height);
  flex-shrink: 0;
  border-top: 1px solid var(--border-primary);
  overflow: hidden;
  transition: height 200ms ease;
}

#bottom-panel-grid.collapsed {
  height: 0;
  border-top: none;
}

.bottom-panel {
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bottom-panel + .bottom-panel {
  border-left: 1px solid var(--border-primary);
}

.bottom-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--border-primary);
  height: 30px;
  flex-shrink: 0;
}

.bottom-panel-title {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.bottom-panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.bottom-panel-ctrl {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 13px;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
  transition: color 0.15s;
}

.bottom-panel-ctrl:hover {
  color: var(--text-primary);
}

/* ============ Panel Inner (shared for overlay panels) ============ */

.panel-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--border-primary);
  height: 34px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-secondary);
}

.panel-close-btn {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
}

.panel-close-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ============ News Feed (RSS articles) ============ */

.feed-filter-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-bottom: 1px solid var(--border-primary);
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
}

.feed-filter-bar::-webkit-scrollbar {
  display: none;
}

.feed-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 9px;
  font-weight: 500;
  font-family: var(--font-ui);
  color: var(--text-muted);
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.feed-filter-chip:hover {
  color: var(--text-secondary);
  border-color: var(--text-muted);
}

.feed-filter-chip.active {
  color: var(--text-primary);
  background: var(--bg-secondary);
  border-color: var(--text-secondary);
}

.feed-watchlist-btn {
  color: #eab308;
  border-color: rgba(234, 179, 8, 0.3);
}

.feed-watchlist-btn:hover {
  border-color: #eab308;
  background: rgba(234, 179, 8, 0.08);
}

.feed-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-primary);
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
}

.feed-tabs::-webkit-scrollbar {
  display: none;
}

.feed-tab {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.feed-tab:hover {
  color: var(--text-secondary);
}

.feed-tab.active {
  color: var(--accent);
  background: var(--accent-bg);
  border-bottom-color: var(--accent);
}

.feed-item {
  padding: var(--spacing-sm) var(--spacing-sm);
  border-bottom: 1px solid var(--border-secondary);
  cursor: pointer;
  transition: background 0.12s;
}

.feed-item:hover {
  background: var(--bg-hover);
}

.feed-item-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-bottom: 3px;
}

.feed-item-badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1px 5px;
  border-radius: 2px;
  flex-shrink: 0;
}

.feed-item-badge.breaking {
  background: var(--severity-critical);
  color: #fff;
  animation: pulse-badge 1.5s ease-in-out infinite;
}

.feed-item-badge.category {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.feed-item-title {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.feed-item-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: 3px;
  font-size: 10px;
  color: var(--text-muted);
}

.feed-item-source {
  font-weight: 500;
  color: var(--text-secondary);
}

.feed-item-severity {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ============ Time Range Pills (news panel header) ============ */

.time-range-overlay {
  display: flex;
  gap: 0;
  margin-left: 10px;
}

.time-range-pill {
  padding: 4px 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3px;
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.15s ease;
  backdrop-filter: blur(8px);
  line-height: 1;
}

.time-range-pill + .time-range-pill {
  border-left: none;
}

.time-range-pill:hover {
  background: rgba(14, 26, 22, 0.95);
  color: var(--text-primary);
}

.time-range-pill.active {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
}

/* ============ Legend Bar (map overlay, bottom center) ============ */

/* ============ Header Enhancements (version, region) ============ */

.header-version {
  font-family: var(--font-mono);
  font-size: 10px;
  color: #e8e8e8;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

/* ============ Layer Control Panel ============ */

.layer-checkbox-native {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.layer-category-header:hover {
  background: var(--bg-hover);
}

.layer-row:hover {
  background: var(--bg-hover);
}

/* ============ Focus Visible ============ */

:focus-visible {
  outline: 2px solid var(--accent-border);
  outline-offset: 2px;
}

button:focus-visible,
input:focus-visible,
select:focus-visible,
a:focus-visible {
  outline: 2px solid var(--accent-border);
  outline-offset: 2px;
}

/* ============ Viewport Warning ============ */

#viewport-warning {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(6px);
  transition: opacity 200ms ease;
}

.viewport-warning-card {
  max-width: 420px;
  padding: 32px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-lg);
  text-align: center;
  box-shadow: var(--shadow-lg);
}

.viewport-warning-icon {
  font-size: 36px;
  margin-bottom: 12px;
  color: var(--accent-amber);
}

.viewport-warning-heading {
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.viewport-warning-message {
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.viewport-warning-hint {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--text-muted);
  margin-bottom: 20px;
}

.viewport-warning-btn {
  padding: 8px 24px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--text-primary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.viewport-warning-btn:hover {
  background: var(--bg-hover);
  border-color: var(--accent);
}

/* ============ Breaking News Banner ============ */

.breaking-banner {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 var(--spacing-md);
  color: #fff;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  overflow: hidden;
  flex-shrink: 0;
  transition: max-height 300ms ease, opacity 300ms ease;
  z-index: 99;
}

.breaking-banner-label {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  flex-shrink: 0;
  opacity: 0.9;
}

.breaking-banner-sep {
  font-size: 10px;
  opacity: 0.6;
  flex-shrink: 0;
}

.breaking-banner-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.breaking-banner-meta {
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.5px;
  opacity: 0.75;
  flex-shrink: 0;
  white-space: nowrap;
}

.breaking-banner-queue {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 1px 5px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}

.breaking-banner-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s;
}

.breaking-banner-close:hover {
  color: #fff;
}

.banner-pulse {
  animation: banner-pulse-anim 2s ease-in-out infinite;
}

@keyframes banner-pulse-anim {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
}

/* ============ Activity Dot ============ */

.activity-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-blue);
  margin-left: 6px;
  flex-shrink: 0;
  animation: activity-dot-pulse 2s ease-in-out infinite;
}

@keyframes activity-dot-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ============ Context Menu ============ */

.context-menu {
  min-width: 180px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 4px 0;
  font-family: var(--font-ui);
  font-size: 12px;
}

.context-menu-header {
  padding: 6px 12px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.context-menu-sep {
  height: 1px;
  background: var(--border-primary);
  margin: 2px 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 12px;
  background: none;
  border: none;
  color: var(--text-secondary);
  font: inherit;
  cursor: pointer;
  text-align: left;
  transition: background 150ms ease, color 150ms ease;
}

.context-menu-item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.context-menu-icon {
  font-size: 13px;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}

/* ============ Country Dossier Panel ============ */

.country-dossier-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(100vh - 150px);
}

.dossier-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
}

.dossier-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-primary);
}

.dossier-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}

.dossier-country-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}

.dossier-country-code {
  font-size: 11px;
  color: var(--text-muted);
  font-family: var(--font-mono);
  letter-spacing: 0.1em;
}

.dossier-export-btn {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
  font-family: inherit;
  font-weight: 600;
  transition: border-color 0.15s;
}

.dossier-export-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.dossier-stats-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.dossier-stat-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
  font-family: var(--font-mono);
}

.dossier-section {
  margin-bottom: 16px;
}

.dossier-section-header {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--border-secondary);
}

.dossier-placeholder {
  font-size: 11px;
  color: var(--text-muted);
  font-style: italic;
  padding: 8px 0;
}

.dossier-card,
.dossier-cii-card {
  padding: 12px;
  border-radius: 6px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
}

.dossier-cii-score-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.dossier-cii-score {
  font-size: 28px;
  font-weight: 700;
  font-family: var(--font-mono);
}

.dossier-cii-class {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
}

.dossier-cii-trend {
  font-size: 11px;
  font-weight: 500;
  margin-left: auto;
}

.dossier-component-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.dossier-bar-label {
  font-size: 10px;
  color: var(--text-secondary);
  width: 80px;
  flex-shrink: 0;
}

.dossier-bar-bg {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--bg-primary);
}

.dossier-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 300ms ease;
}

.dossier-bar-value {
  font-size: 10px;
  font-family: var(--font-mono);
  color: var(--text-muted);
  width: 24px;
  text-align: right;
}

.dossier-timestamp {
  font-size: 9px;
  color: var(--text-muted);
  margin-top: 8px;
  text-align: right;
}

.dossier-boost-badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--accent-bg);
  color: var(--accent);
  font-family: var(--font-mono);
}

.dossier-signal-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dossier-signal-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  font-size: 11px;
}

.dossier-signal-type {
  color: var(--text-primary);
  text-transform: capitalize;
}

.dossier-signal-count {
  color: var(--accent);
  font-weight: 600;
  font-family: var(--font-mono);
}

.dossier-convergence-row {
  padding: 6px 0;
  border-bottom: 1px solid var(--border-secondary);
}

.dossier-convergence-label {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
}

.dossier-convergence-types {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 2px;
}

.dossier-type-chip {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.dossier-convergence-stats {
  font-size: 10px;
  color: var(--text-muted);
}

.dossier-type-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
}

.dossier-infra-category {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 8px 0 4px 0;
}

.dossier-infra-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 8px;
  font-size: 11px;
}

.dossier-infra-name {
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.dossier-infra-distance {
  flex-shrink: 0;
  color: var(--text-muted);
  font-family: var(--font-mono);
  font-size: 10px;
  margin-left: 8px;
}

.dossier-event-list {
  max-height: 300px;
  overflow-y: auto;
}

.dossier-news-item {
  padding: 6px 0;
  border-bottom: 1px solid var(--border-secondary);
}

.dossier-news-title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.dossier-news-time {
  font-size: 10px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.dossier-news-desc {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.4;
  margin-bottom: 2px;
}

.dossier-news-source {
  font-size: 9px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ============ TV/Kiosk Mode ============ */

.tv-mode-active #header-bar,
.tv-mode-active #sub-header-bar,
.tv-mode-active #floating-layers,
.tv-mode-active #overlay-right {
  display: none !important;
}

.tv-mode-active #bottom-panel-grid {
  height: 30vh;
  border-top: 1px solid var(--border-primary);
}

.tv-mode-active #map-region {
  flex: 1;
}

.tv-mode-active #map-container {
  height: 100% !important;
}

.tv-mode-exit-hint {
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 9999;
  font-size: 11px;
  color: var(--text-muted);
  background: rgba(0, 0, 0, 0.6);
  padding: 4px 10px;
  border-radius: 4px;
  pointer-events: none;
  opacity: 0.7;
  transition: opacity 2s ease;
}

/* ============ Print CSS ============ */

@media print {
  #header-bar,
  #sub-header-bar,
  .breaking-banner,
  .panel-close-btn,
  .sub-header-btn,
  #map-container,
  #bottom-panel-grid,
  #panel-left,
  .context-menu,
  .search-modal-overlay {
    display: none !important;
  }

  body {
    background: #fff !important;
    color: #000 !important;
    font-size: 11pt;
  }

  #panel-right {
    position: static !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    overflow: visible !important;
    border: none !important;
    background: #fff !important;
  }

  #panel-right .panel-inner {
    display: block !important;
    height: auto !important;
    page-break-inside: avoid;
  }

  #panel-right .panel-header {
    border-bottom: 1px solid #ccc;
    padding: 8px 0;
    font-size: 14pt;
    font-weight: bold;
  }

  #panel-right .panel-body {
    overflow: visible !important;
    max-height: none !important;
  }

  .severity-critical { color: #c0392b !important; }
  .severity-high { color: #e67e22 !important; }
  .severity-medium { color: #f1c40f !important; }
  .severity-low { color: #27ae60 !important; }
}

/* ============ Threat Tempo Badge ============ */

.tempo-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 2px 10px;
  border-radius: 10px;
  border: 1px solid;
  white-space: nowrap;
  flex-shrink: 0;
  text-transform: uppercase;
  line-height: 1.3;
  transition: color 0.4s, border-color 0.4s;
  cursor: default;
}

.tempo-arrow {
  font-size: 8px;
  line-height: 1;
}

.tempo-label {
  font-size: 10px;
}

.tempo-flash {
  animation: tempo-flash-anim 0.6s ease-out;
}

@keyframes tempo-flash-anim {
  0% { transform: scale(1.15); filter: brightness(1.6); }
  100% { transform: scale(1); filter: brightness(1); }
}

/* ============ Intel Badge ============ */

.intel-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border-primary);
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
  line-height: 1.3;
  transition: background 0.3s, color 0.3s, border-color 0.3s;
}

.intel-badge-icon {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.intel-badge--active {
  background: rgba(245, 158, 11, 0.2);
  color: var(--accent-amber);
  border-color: rgba(245, 158, 11, 0.5);
}

.intel-badge--critical {
  background: rgba(239, 68, 68, 0.25);
  color: #fff;
  border-color: var(--accent-red);
  animation: intel-pulse 2s ease-in-out infinite;
}

@keyframes intel-pulse {
  0%, 100% { background: rgba(239, 68, 68, 0.25); }
  50% { background: rgba(239, 68, 68, 0.4); }
}

/* ============ Signal Indicator ============ */

.signal-indicator {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
}

/* ============ Export Dropdown ============ */

.export-dropdown-container {
  position: relative;
  flex-shrink: 0;
}

.export-dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  z-index: 200;
  min-width: 120px;
  overflow: hidden;
}

.export-dropdown-menu.visible {
  display: block;
}

.export-dropdown-item {
  display: block;
  width: 100%;
  padding: 6px 12px;
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--text-secondary);
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}

.export-dropdown-item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.export-dropdown-item + .export-dropdown-item {
  border-top: 1px solid var(--border-secondary);
}

/* ============ Map Controls (top-right stack) ============ */

.map-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 12;
  display: flex;
  flex-direction: column;
  /* Shrink-wrap each group to its own content — without this the wide
     2D/3D toggle stretches every single-column group below it. */
  align-items: flex-end;
  gap: 6px;
  pointer-events: auto;
}

.map-ctrl-group {
  display: flex;
  flex-direction: column;
  background: rgba(2, 10, 8, 0.88);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  backdrop-filter: blur(8px);
  overflow: hidden;
}

.map-ctrl-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
  padding: 0;
}

.map-ctrl-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.map-ctrl-btn + .map-ctrl-btn {
  border-top: 1px solid var(--border-secondary);
}

.map-ctrl-btn svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.map-ctrl-compass svg {
  transition: transform 0.3s ease;
}

/* 2D/3D mode toggle — horizontal segmented pair at the top of the stack */
.map-ctrl-group.map-mode-toggle {
  flex-direction: row;
}

.map-mode-btn {
  width: auto;
  min-width: 32px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  font-family: var(--font-mono, monospace);
}

.map-mode-btn + .map-mode-btn {
  border-top: none;
  border-left: 1px solid var(--border-secondary);
}

/* Flat-map-only tools are hidden while the 3D globe is active. The
   tools group hides as a whole — all its buttons are flat-only, and an
   empty bordered group would render as a stray line. */
.map-controls.globe-mode .map-ctrl-compass,
.map-controls.globe-mode .map-tools-group {
  display: none;
}

/* ============ View Preset Dropdown ============ */

.view-preset-dropdown {
  position: absolute;
  right: 38px;
  top: 0;
  background: rgba(2, 10, 8, 0.95);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  backdrop-filter: blur(8px);
  overflow: hidden;
  min-width: 130px;
  z-index: 20;
}

.view-preset-item {
  display: block;
  width: 100%;
  padding: 6px 12px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 11px;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}

.view-preset-item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.view-preset-item + .view-preset-item {
  border-top: 1px solid var(--border-secondary);
}

/* ============ deck.gl Tooltip ============ */

.deckgl-tooltip {
  max-width: 250px !important;
  font-size: 11px !important;
  font-family: var(--font-mono) !important;
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  border: 1px solid var(--border-primary) !important;
  border-radius: var(--radius-sm) !important;
  padding: 6px 10px !important;
  pointer-events: none;
}

.deckgl-tooltip strong {
  color: var(--accent);
}

/* ============ Coordinate Readout ============ */

.coord-readout {
  position: absolute;
  bottom: 8px;
  left: 12px;
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(2, 10, 8, 0.88);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  backdrop-filter: blur(8px);
  padding: 4px 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  letter-spacing: 0.3px;
  user-select: none;
  transition: opacity 0.2s ease;
}

.coord-readout-text {
  cursor: pointer;
  transition: color 0.15s;
  min-width: 200px;
  text-align: center;
}

.coord-readout-text:hover {
  color: var(--text-primary);
}

.coord-readout-text.coord-copied {
  color: var(--accent-green);
}

.coord-readout-toggle {
  border: none;
  background: var(--bg-hover);
  color: var(--text-muted);
  font-family: var(--font-mono);
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.coord-readout-toggle:hover {
  background: var(--border-primary);
  color: var(--text-primary);
}

/* ============ Measure Tool ============ */

.map-ctrl-btn.active {
  background: var(--accent-bg);
  color: var(--accent);
}

.measure-point {
  position: absolute;
  width: 10px;
  height: 10px;
  background: var(--accent, #06b6d4);
  border: 2px solid white;
  border-radius: 50%;
  pointer-events: none;
  z-index: 20;
  box-shadow: 0 0 6px rgba(6, 182, 212, 0.6);
}

/* ============ Bookmark Locations ============ */

.bookmark-dropdown {
  z-index: 100;
  background: rgba(2, 10, 8, 0.95);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  backdrop-filter: blur(12px);
  min-width: 200px;
  max-width: 280px;
  max-height: 320px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.bookmark-dropdown-header {
  padding: 8px 12px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border-secondary);
}

.bookmark-empty {
  padding: 12px;
  font-size: 11px;
  color: var(--text-dim);
  text-align: center;
}

.bookmark-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px 2px 0;
  border-bottom: 1px solid var(--border-secondary);
}

.bookmark-row:last-of-type {
  border-bottom: none;
}

.bookmark-name {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 12px;
  padding: 6px 12px;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bookmark-name:hover {
  background: var(--bg-hover);
  color: var(--accent);
}

.bookmark-delete {
  border: none;
  background: transparent;
  color: var(--text-dim);
  cursor: pointer;
  padding: 4px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.bookmark-delete:hover {
  color: var(--accent-red);
  background: rgba(239, 68, 68, 0.1);
}

.bookmark-save-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 11px;
  padding: 8px 12px;
  cursor: pointer;
  border-top: 1px solid var(--border-secondary);
  transition: background 0.12s, color 0.12s;
}

.bookmark-save-btn:hover {
  background: var(--bg-hover);
  color: var(--accent);
}

.bookmark-input-row {
  display: flex;
  gap: 4px;
  padding: 6px 8px;
  border-top: 1px solid var(--border-secondary);
}

.bookmark-input {
  flex: 1;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 3px;
  outline: none;
}

.bookmark-input:focus {
  border-color: var(--accent);
}

.bookmark-confirm-btn {
  border: none;
  background: var(--accent-bg);
  color: var(--accent);
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.12s;
}

.bookmark-confirm-btn:hover {
  background: var(--accent-border);
}

.measure-result {
  position: absolute;
  transform: translateX(-50%);
  z-index: 20;
  background: rgba(2, 10, 8, 0.92);
  border: 1px solid var(--accent-border);
  border-radius: var(--radius-sm);
  padding: 4px 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--accent);
  white-space: nowrap;
  pointer-events: none;
  backdrop-filter: blur(8px);
  letter-spacing: 0.3px;
}

/* ============ Time Slider ============ */

.time-slider {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(2, 10, 8, 0.92);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  pointer-events: auto;
  max-width: 600px;
  width: 80%;
  min-width: 360px;
}

.time-slider-play {
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.time-slider-play:hover {
  color: var(--accent);
  background: var(--accent-bg);
}

.time-slider-label {
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--text-dim);
  white-space: nowrap;
  flex-shrink: 0;
}

.time-slider-track {
  flex: 1;
  height: 24px;
  position: relative;
  cursor: pointer;
  border-radius: 2px;
  overflow: hidden;
  background: var(--bg-primary);
}

.time-slider-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(6, 182, 212, 0.08);
  pointer-events: none;
}

.time-slider-histogram {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  gap: 1px;
  padding: 2px;
  pointer-events: none;
}

.time-slider-bar {
  flex: 1;
  background: var(--accent);
  opacity: 0.6;
  border-radius: 1px 1px 0 0;
  min-height: 0;
  transition: opacity 0.15s;
}

.time-slider-playhead {
  position: absolute;
  top: 0;
  left: 100%;
  width: 2px;
  height: 100%;
  background: var(--accent);
  transform: translateX(-1px);
  pointer-events: none;
  box-shadow: 0 0 4px rgba(6, 182, 212, 0.5);
}

.time-slider-cursor-label {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--accent);
  white-space: nowrap;
  min-width: 80px;
  text-align: center;
  flex-shrink: 0;
}

.time-slider-live {
  border: none;
  background: transparent;
  color: var(--text-dim);
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 3px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.time-slider-live.active {
  color: var(--accent-green);
  background: rgba(34, 197, 94, 0.15);
}

.time-slider-live:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

/* ============ Checkbox Accent ============ */

input[type="checkbox"] {
  accent-color: var(--accent);
}

/* ============ LIVE Dot (red, for bottom panel titles) ============ */

.live-dot-red {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-red);
  box-shadow: 0 0 4px var(--accent-red);
  animation: live-pulse-red 2s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes live-pulse-red {
  0%, 100% { opacity: 1; box-shadow: 0 0 6px var(--accent-red); }
  50% { opacity: 0.5; box-shadow: 0 0 2px var(--accent-red); }
}

/* ============ Scrollbar ============ */

::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}
::-webkit-scrollbar-thumb {
  background: var(--border-primary);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

/* ============ Bottom Panel Tab Bar ============ */

.bottom-tab-bar {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  height: 30px;
  flex-shrink: 0;
  overflow: hidden;
}

.bottom-tab {
  padding: 0 14px;
  height: 100%;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-muted);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
}

.bottom-tab:hover {
  color: var(--text-secondary);
}

.bottom-tab.active {
  color: var(--text-primary);
  border-bottom-color: var(--accent);
}

.bottom-tab .tab-badge {
  font-family: var(--font-mono);
  font-size: 8px;
  font-weight: 700;
  background: var(--accent-red);
  color: #fff;
  border-radius: 8px;
  padding: 1px 5px;
  margin-left: 5px;
  line-height: 1.2;
}

.bottom-tab-spacer {
  flex: 1;
}

.bottom-tab-time-pills {
  display: flex;
  gap: 0;
  margin-right: 8px;
}

.bottom-tab-time-pills .time-range-pill:first-child {
  border-radius: 3px 0 0 3px;
}

.bottom-tab-time-pills .time-range-pill:last-child {
  border-radius: 0 3px 3px 0;
}

/* ============ Bottom Panel Tab Pages ============ */

.bottom-tab-page {
  display: none;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.bottom-tab-page.active {
  display: block;
}

/* ============ Situation Board ============ */

.sit-section {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-secondary);
}

.sit-section:last-child {
  border-bottom: none;
}

.sit-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
  cursor: pointer;
  user-select: none;
}

.sit-header:hover .sit-label {
  color: var(--text-secondary);
}

.sit-label {
  font-family: var(--font-ui);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--text-muted);
  transition: color 0.15s;
}

.sit-count {
  font-family: var(--font-mono);
  font-size: 8px;
  color: var(--text-muted);
  background: var(--bg-tertiary);
  padding: 1px 5px;
  border-radius: 8px;
}

.sit-chevron {
  font-size: 8px;
  color: var(--text-dim);
  margin-left: auto;
  transition: transform 0.15s;
}

.sit-section.collapsed .sit-chevron {
  transform: rotate(-90deg);
}

.sit-section.collapsed .sit-body {
  display: none;
}

/* Breaking card */
.breaking-card {
  background: linear-gradient(135deg, rgba(239,68,68,0.10) 0%, rgba(239,68,68,0.03) 100%);
  border: 1px solid rgba(239,68,68,0.25);
  border-radius: var(--radius-sm);
  padding: 7px 10px;
  cursor: pointer;
  transition: border-color 0.15s;
  margin-bottom: 4px;
}

.breaking-card:hover {
  border-color: rgba(239,68,68,0.5);
}

.breaking-label {
  font-family: var(--font-ui);
  font-size: 8px;
  font-weight: 800;
  color: var(--accent-red);
  letter-spacing: 1px;
  text-transform: uppercase;
  animation: sit-pulse 2s ease-in-out infinite;
}

@keyframes sit-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.breaking-title {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--text-primary);
  margin-top: 2px;
  line-height: 1.35;
}

.breaking-meta {
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--text-muted);
  margin-top: 3px;
}

/* Focal point cards */
.focal-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.12s;
}

.focal-card:hover {
  background: rgba(6, 182, 212, 0.06);
}

.focal-score {
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.focal-score.crit {
  background: rgba(239,68,68,0.15);
  color: var(--accent-red);
}

.focal-score.elev {
  background: rgba(245,158,11,0.15);
  color: var(--accent-amber);
}

.focal-score.watch {
  background: rgba(6,182,212,0.12);
  color: var(--accent);
}

.focal-body {
  flex: 1;
  min-width: 0;
}

.focal-entity {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 600;
  color: var(--text-primary);
}

.focal-desc {
  font-family: var(--font-ui);
  font-size: 9px;
  color: var(--text-muted);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.focal-tags {
  display: flex;
  gap: 3px;
  margin-top: 3px;
}

.focal-tag {
  font-family: var(--font-ui);
  font-size: 7px;
  padding: 1px 4px;
  border-radius: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.focal-tag.conflict { background: rgba(239,68,68,0.12); color: #f87171; }
.focal-tag.cyber { background: rgba(168,85,247,0.12); color: #c084fc; }
.focal-tag.maritime { background: rgba(6,182,212,0.12); color: #22d3ee; }
.focal-tag.economic { background: rgba(245,158,11,0.12); color: #fbbf24; }
.focal-tag.nuclear { background: rgba(168,85,247,0.12); color: #c084fc; }
.focal-tag.infrastructure { background: rgba(100,116,139,0.12); color: #94a3b8; }
.focal-tag.diplomatic { background: rgba(34,197,94,0.12); color: #4ade80; }
.focal-tag.military { background: rgba(239,68,68,0.12); color: #f87171; }
.focal-tag.political { background: rgba(59,130,246,0.12); color: #60a5fa; }
.focal-tag.social { background: rgba(245,158,11,0.12); color: #fbbf24; }
.focal-tag.environmental { background: rgba(34,197,94,0.12); color: #4ade80; }
.focal-tag.health { background: rgba(34,197,94,0.12); color: #4ade80; }
.focal-tag.aviation { background: rgba(59,130,246,0.12); color: #60a5fa; }

/* Surge rows */
.surge-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
}

.surge-row:hover {
  background: rgba(245,158,11,0.05);
}

.surge-arrow {
  font-size: 12px;
  color: var(--accent-red);
}

.surge-info {
  flex: 1;
  min-width: 0;
}

.surge-type {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 600;
  color: var(--text-primary);
}

.surge-region {
  font-family: var(--font-ui);
  font-size: 8px;
  color: var(--text-muted);
}

.surge-val {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--accent-red);
}

/* CII rows */
.cii-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 2px;
}

.cii-row:hover {
  background: rgba(6,182,212,0.05);
}

.cii-code {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  color: var(--text-muted);
  min-width: 20px;
}

.cii-bar {
  flex: 1;
  height: 5px;
  background: var(--bg-tertiary);
  border-radius: 3px;
  overflow: hidden;
}

.cii-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.cii-val {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  min-width: 22px;
  text-align: right;
}

.cii-trend {
  font-family: var(--font-mono);
  font-size: 9px;
  min-width: 10px;
}

/* Convergence rows */
.conv-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 2px;
}

.conv-row:hover {
  background: rgba(168,85,247,0.05);
}

.conv-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-purple);
  flex-shrink: 0;
}

.conv-name {
  font-family: var(--font-ui);
  font-size: 10px;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conv-types {
  font-family: var(--font-mono);
  font-size: 8px;
  color: var(--accent-purple);
  flex-shrink: 0;
}

/* Source health summary (in situation tab) */
.src-summary {
  display: flex;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--text-secondary);
}

.src-stat {
  display: flex;
  align-items: center;
  gap: 3px;
}

.src-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

.src-dot.live { background: var(--accent-green); }
.src-dot.stale { background: var(--accent-amber); }
.src-dot.dead { background: var(--accent-red); }

/* ============ Source Health Panel (Sources tab) ============ */

.source-health-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.src-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border-secondary);
}

.src-row:last-child {
  border-bottom: none;
}

.src-name {
  font-family: var(--font-ui);
  font-size: 10px;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
}

.src-status-badge {
  font-family: var(--font-ui);
  font-size: 8px;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: capitalize;
  flex-shrink: 0;
}

.src-status-badge.live { background: rgba(34,197,94,0.12); color: var(--accent-green); }
.src-status-badge.stale { background: rgba(245,158,11,0.12); color: var(--accent-amber); }
.src-status-badge.dead { background: rgba(239,68,68,0.12); color: var(--accent-red); }

.src-age {
  font-family: var(--font-mono);
  font-size: 9px;
  color: var(--text-dim);
  min-width: 50px;
  text-align: right;
  flex-shrink: 0;
}

/* Situation empty state */
.sit-empty {
  padding: 24px 16px;
  text-align: center;
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--text-muted);
}

/* Cached data banner (warm start) */
.sit-cached-banner {
  padding: 4px 12px;
  font-family: var(--font-ui);
  font-size: 10px;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid var(--border-primary);
  text-align: center;
  letter-spacing: 0.02em;
}

/* ============ Session 10: Panel Picker ============ */

.panel-picker {
  position: relative;
  display: flex;
  align-items: center;
}

.panel-picker-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  margin-left: 4px;
}

.panel-picker-btn:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.panel-picker-dropdown {
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 320px;
  max-height: min(460px, calc(100vh - 80px));
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 200;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.15s, transform 0.15s;
  margin-bottom: 6px;
}

.panel-picker-dropdown.visible {
  opacity: 1;
  transform: translateY(0);
}

.panel-picker-header {
  padding: 8px 12px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-primary);
  flex-shrink: 0;
}

.panel-picker-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-picker-section {
  padding: 4px 0;
}

.panel-picker-category {
  padding: 4px 12px 2px;
  font-family: var(--font-ui);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-dim);
}

.panel-picker-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 0.1s;
}

.panel-picker-row:hover {
  background: var(--bg-hover);
}

.panel-picker-checkbox {
  margin-top: 2px;
  accent-color: var(--accent);
  flex-shrink: 0;
}

.panel-picker-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.panel-picker-name {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 500;
  color: var(--text-primary);
}

.panel-picker-desc {
  font-family: var(--font-ui);
  font-size: 10px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-picker-footer {
  padding: 6px 12px;
  border-top: 1px solid var(--border-primary);
  flex-shrink: 0;
}

.panel-picker-reset {
  background: none;
  border: none;
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 10px;
  cursor: pointer;
  padding: 2px 0;
  transition: color 0.15s;
}

.panel-picker-reset:hover {
  color: var(--accent);
}

/* ============ Session 10: Panel Header Buttons ============ */

.panel-header-btns {
  display: flex;
  align-items: center;
  gap: 2px;
}

.panel-popout-btn {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
}

.panel-popout-btn:hover {
  background: var(--bg-hover);
  color: var(--accent);
}

/* ============ Session 10: Bottom Panel Drag-to-Reorder ============ */

.bottom-tab[draggable="true"] {
  cursor: grab;
}

.bottom-tab[draggable="true"]:active {
  cursor: grabbing;
}

.bottom-tab.drag-over {
  border-bottom-color: var(--accent);
  background: var(--accent-bg);
}

.bottom-tab.dragging {
  opacity: 0.4;
}

/* ============ Session 10: Bottom Panel Resize ============ */

.bottom-panel-resize-handle {
  position: absolute;
  top: -3px;
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
  z-index: 10;
  background: transparent;
  transition: background 0.15s;
}

.bottom-panel-resize-handle:hover,
.bottom-panel-resize-handle.active {
  background: var(--accent);
  opacity: 0.5;
}

#bottom-panel-grid {
  position: relative;
}

/* ============ Session 10: Desk Preset Editor ============ */

.preset-editor-section {
  padding: 12px;
  border-top: 1px solid var(--border-primary);
}

.preset-editor-title {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.preset-editor-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.preset-editor-name {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--text-primary);
  flex: 1;
}

.preset-editor-btn {
  background: none;
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
  font-family: var(--font-ui);
  font-size: 10px;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.15s;
}

.preset-editor-btn:hover {
  border-color: var(--accent-border);
  color: var(--accent);
  background: var(--accent-bg);
}

.preset-editor-btn.danger:hover {
  border-color: var(--accent-red);
  color: var(--accent-red);
  background: rgba(239, 68, 68, 0.1);
}

.preset-editor-input {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-family: var(--font-ui);
  font-size: 11px;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  outline: none;
  transition: border-color 0.15s;
}

.preset-editor-input:focus {
  border-color: var(--accent-border);
}

.preset-editor-input::placeholder {
  color: var(--text-dim);
}
`;

